import React from 'react';
import classnames from 'classnames';
import * as mobxReact from 'mobx-react';
import { IConnectionDetailsDTO, IConnectionGroupDTO, IParentConnectionDTO, onChangeConnectionEntity } from '../../../../../api/connections.dao';
import { OrganizationMeetingConnection } from '../../../../../modules/connections/meeting/organization-meeting-connection.aggregate';
import { ConnectionDropdownPresentation } from '../../shared/_connection-dropdown/connection-dropdown.presentation';
import Text from '../../../../global/text/text';
import { AttendeeConnection } from './attendee-connection';
import { ConnectionVersion } from '../../../../../constants/common.constants';
import { CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import { ChildConnectionsViewModel } from '../_meeting-connections-form/child-connections/child-connections.viewmodel';
import { IDropdownConnectionGroupDetails } from './attendee-unique-connection-indicator-wrapper/attendee-unique-connection-group-indicator/attendee-unique-connection-group-indicator';

export type OrganizationConnectionProps = {
    name: string;
    entity: OrganizationMeetingConnection;
    removeOrganization?: (organizationId: number) => void;
    removeAttendee?: (attendeeId: number) => void;
    meetingConnections: IConnectionDetailsDTO[];
    connectionGroups: IConnectionGroupDTO[];
    filteredMeetingConnections: IConnectionDetailsDTO[];
    derivedConnection?: null | IConnectionDetailsDTO;
    editConnection?: (connection: IConnectionDetailsDTO) => void;
    onChangeMeetingConnection: (
        entity: onChangeConnectionEntity,
        connectionId: number | null,
        changePresenterOrHost?: boolean,
        ) => void;
    isRoleRegistrant?: boolean;
    knovioReplayConnection?: null | IConnectionDetailsDTO;
    disabledDropDownConnection?: boolean;
    connectionVersion: ConnectionVersion;
    shouldRenderUniqueSection?: boolean;
    meetingParentConnections: IParentConnectionDTO[];
    handleTrackChildCopyBtn?: (event: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, type: string) => void;
    childConnectionsVM: ChildConnectionsViewModel;
    getConnectionsListFromGroup: (assignedGroup: IConnectionGroupDTO, attendeeId: number) => IDropdownConnectionGroupDetails[];
};

export const OrganizationConnection: React.FC<OrganizationConnectionProps> = mobxReact.observer((props: OrganizationConnectionProps): JSX.Element => {
    const {
        entity,
        removeOrganization,
        removeAttendee,
        editConnection,
        onChangeMeetingConnection,
        filteredMeetingConnections,
        isRoleRegistrant,
        knovioReplayConnection,
        disabledDropDownConnection,
        connectionVersion,
        connectionGroups,
        shouldRenderUniqueSection,
        meetingParentConnections,
        handleTrackChildCopyBtn,
        childConnectionsVM,
        getConnectionsListFromGroup,
    } = props;
    const handleRemoveOrganization = () => removeOrganization(entity.getId());
    const onChangeConnection = ((connectionId: number | null) => {
        onChangeMeetingConnection(entity, connectionId);
    });
    const assignedConnectionId = entity.hasAssignedConnection() ? entity.getAssignedConnection() : null;

    const attendeeDerivedConnection = assignedConnectionId
        ? props.meetingConnections.concat(connectionGroups).find(item => item.meetingConnectionId === assignedConnectionId)
        : props.derivedConnection
    ;

    return (
        <div>
            <div className='organization__header'>
                <div className={classnames('organization__header-name', 'organization__header-name--active')}>
                    <Text className='organization__title' tag='label' message={entity.getProps().name} withEllipsis />
                    { !isRoleRegistrant && (
                        <ConnectionDropdownPresentation
                            meetingConnections={filteredMeetingConnections}
                            derivedConnection={props.derivedConnection}
                            assignedConnectionId={assignedConnectionId}
                            isLabelVisibleCondition={Boolean(knovioReplayConnection || assignedConnectionId || props.derivedConnection)}
                            onChange={onChangeConnection}
                            knovioReplayConnection={knovioReplayConnection}
                            disabledDropDownConnection={disabledDropDownConnection}
                            connectionVersion={connectionVersion}
                            connectionGroups={connectionGroups}
                            cssClass={shouldRenderUniqueSection ? 'w-150' : ''}
                        />
                    )}
                </div>
                {removeOrganization &&
                    <Text
                        className='organization__remove-label'
                        description='Button label'
                        id='oe.common.remove'
                        message='Remove'
                        tag='label'
                        onClick={handleRemoveOrganization}
                    />
                }
            </div>
            {entity.getEntities()
                .slice()
                .sort((a, b) => a.getProps().lastName.localeCompare(b.getProps().lastName))
                .map(attendee => {
                    return (
                        <AttendeeConnection
                            entity={attendee}
                            key={attendee.getId()}
                            removeAttendee={removeAttendee}
                            meetingConnections={props.meetingConnections}
                            derivedConnection={attendeeDerivedConnection}
                            editConnection={editConnection}
                            onChangeMeetingConnection={onChangeMeetingConnection}
                            isRoleRegistrant={isRoleRegistrant}
                            knovioReplayConnection={knovioReplayConnection}
                            disabledDropDownConnection={disabledDropDownConnection}
                            connectionVersion={connectionVersion}
                            connectionGroups={connectionGroups}
                            shouldRenderUniqueSection={shouldRenderUniqueSection}
                            meetingParentConnections={meetingParentConnections}
                            handleTrackChildCopyBtn={handleTrackChildCopyBtn}
                            childConnectionsVM={childConnectionsVM}
                            getConnectionsListFromGroup={getConnectionsListFromGroup}
                        />
                    );
                })
            }
        </div>
    );
});

